import React, {useEffect, useState} from 'react';

import {Tooltip, PrescriptionTooltip} from '@optimaxdev/design-system';
import {OdAndOsType} from '@optimaxdev/design-system/dist/types/components/prescriptionTable/types';
import classNames from 'classnames';

import {PRESCRIPTION_METHOD} from 'constants/product';
import {ContactLensesNameType} from 'desktop/pages/cart/types/quoteItem';
import {apiConfig} from 'libs/api/api';
import {logWithSentry} from 'libs/sentry/sentry';

import s from './prescriptionViewTooltip.scss';

export type PropsType = {
    link: string;
    os: OdAndOsType;
    od: OdAndOsType;
    name: ContactLensesNameType;
    method: number;
};

/**
 * Returns a tooltip depending on the method of sending the prescription
 * @param {string} link - image url
 * @param {OdAndOsType} os - information for building a prescription table
 * @param {OdAndOsType} od - information for building a prescription table
 * @param {string} name - name of the prescription. Needed only for a tooltip with a table
 * @param {string} method - name of the method, the tooltip is rendered based on it
 * @constructor
 */
export const PrescriptionViewTooltip = ({link, method, name, od, os}: PropsType) => {
    const [src, setSrc] = useState('');
    const [isImage, setIsImage] = useState(false);

    const openFileInNewTab = () => {
        try {
            const tab = window.open(src, '_blank', 'noopener,noreferrer');
            tab?.focus();
        } catch (e) {
            logWithSentry(e);
        }
    };

    useEffect(() => {
        if (method === PRESCRIPTION_METHOD.UPLOAD_METHOD && link)
            apiConfig
                .instanceGusaBackend(link, {baseURL: '', responseType: 'blob'})
                .then(response => {
                    const mimeType = response.data.type;
                    const imgUrl = URL.createObjectURL(response.data);
                    setSrc(imgUrl);
                    setIsImage(mimeType.startsWith('image/'));
                })
                .catch(e => {
                    logWithSentry(e);
                });
    }, []);

    switch (method) {
        case PRESCRIPTION_METHOD.FILL_IT_ONLINE_METHOD:
            return (
                <PrescriptionTooltip
                    name={name}
                    od={od}
                    os={os}
                    tooltipSettings={{children: '', content: '', position: 'bottom'}}
                >
                    <p className={s.view}>View Prescription</p>
                </PrescriptionTooltip>
            );
        case PRESCRIPTION_METHOD.UPLOAD_METHOD:
            return (
                <Tooltip
                    content={
                        isImage ? (
                            // eslint-disable-next-line react/forbid-elements, jsx-a11y/img-redundant-alt
                            <img
                                alt="prescription-image"
                                className={classNames(s.tooltip, s.photo)}
                                src={src}
                            />
                        ) : null
                    }
                    openTrigger={isImage ? 'click' : 'none'}
                    position="bottom"
                >
                    <p
                        className={classNames(s.view, {[s.link]: isImage})}
                        {...(!isImage && {onClick: openFileInNewTab})}
                    >
                        View Prescription
                    </p>
                </Tooltip>
            );
        case PRESCRIPTION_METHOD.SEND_EMAIL_OR_FAX_METHOD:
            return (
                <Tooltip
                    content={
                        <p className={classNames(s.tooltip, s.later)}>
                            Send us your prescription via email or fax after completing your order.
                            Remember, we can’t ship your contacts without it.
                        </p>
                    }
                    position="bottom"
                >
                    <p className={s.view}>Send prescription later</p>
                </Tooltip>
            );
        default:
            return null;
    }
};
